import { Box, Typography, Paper } from '@mui/material';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import DatasetIcon from '@mui/icons-material/DatasetOutlined';
import ViewWeekIcon from '@mui/icons-material/ViewWeekOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import CropSquareIcon from '@mui/icons-material/CropSquareOutlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import TagIcon from '@mui/icons-material/TagOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type ToolboxObjectType =
  | 'text'
  | 'dynamic'
  | 'barcode'
  | 'qr'
  | 'logo'
  | 'line'
  | 'rectangle'
  | 'ellipse'
  | 'dateTime'
  | 'serialNumber'
  | 'table';

// Custom MIME type (not 'text/plain') so the canvas's drop handler only reacts to drags that
// actually originated from this toolbox, not e.g. a stray text/image drag from elsewhere in the
// browser landing on the canvas and being misread as an object type.
export const TOOLBOX_DND_MIME = 'application/x-label-toolbox-object';

interface ToolboxItem {
  type: ToolboxObjectType;
  labelKey: string;
  icon: ComponentType<SvgIconProps>;
}

const GROUPS: { sectionKey: string; items: ToolboxItem[] }[] = [
  {
    sectionKey: 'general',
    items: [
      { type: 'text', labelKey: 'text', icon: TextFieldsIcon },
      { type: 'dynamic', labelKey: 'dynamic', icon: DatasetIcon },
      { type: 'dateTime', labelKey: 'dateTime', icon: EventOutlinedIcon },
      { type: 'serialNumber', labelKey: 'serialNumber', icon: TagIcon },
    ],
  },
  {
    sectionKey: 'barcodes',
    items: [
      { type: 'barcode', labelKey: 'barcode', icon: ViewWeekIcon },
      { type: 'qr', labelKey: 'qr', icon: QrCode2Icon },
    ],
  },
  {
    sectionKey: 'shapesMedia',
    items: [
      { type: 'logo', labelKey: 'logo', icon: ImageIcon },
      { type: 'line', labelKey: 'line', icon: HorizontalRuleIcon },
      { type: 'rectangle', labelKey: 'rectangle', icon: CropSquareIcon },
      { type: 'ellipse', labelKey: 'ellipse', icon: CircleOutlinedIcon },
    ],
  },
  {
    sectionKey: 'data',
    items: [{ type: 'table', labelKey: 'table', icon: TableRowsOutlinedIcon }],
  },
];

interface ToolboxProps {
  onAddObject: (type: ToolboxObjectType) => void;
}

export default function Toolbox({ onAddObject }: ToolboxProps) {
  const { t } = useTranslation('labelDesigner');
  return (
    <Box sx={{ width: 240, borderRight: '1px solid', borderColor: 'divider', p: 2, overflowY: 'auto' }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t('toolbox.components')}
      </Typography>
      {GROUPS.map((group) => (
        <Box key={group.sectionKey} sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t(`toolbox.sections.${group.sectionKey}`)}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <GridContainer columns={12}>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <GridItem xs={6} key={item.type}>
                    <Paper
                      variant="outlined"
                      onClick={() => onAddObject(item.type)}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(TOOLBOX_DND_MIME, item.type);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      sx={{ p: 1.5, textAlign: 'center', cursor: 'grab', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
                    >
                      <Icon fontSize="small" sx={{ color: 'text.secondary', mb: 0.5 }} />
                      <Typography variant="caption" display="block">
                        {t(`toolbox.items.${item.labelKey}`)}
                      </Typography>
                    </Paper>
                  </GridItem>
                );
              })}
            </GridContainer>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
